/* eslint-disable react/require-optimization */
import * as React from "react";
import {
  RefetchOptions,
  RelayRefetchProp,
  createRefetchContainer,
  commitMutation,
  graphql,
} from "react-relay";
import styled from "styled-components";
import { getCssValue } from "~/lib/cssValues";
import classNames from "classnames";

import Environment from "~/lib/relay/environment";
import cable from "~/lib/cable";
import permissions from "~/lib/permissions";

import Favorite from "~/components/icons/Favorite";
import Emojify from "~/components/shared/Emojify";
import Dropdown from "~/components/shared/Dropdown";
import Icon from "~/components/shared/Icon";
import PipelineStatus from "~/components/shared/PipelineStatus";

import Status from "~/components/OrganizationShow/Pipeline/Status";
import Metrics from "~/components/OrganizationShow/Pipeline/Metrics";
import Tag from "~/components/OrganizationShow/Pipeline/Tag";
import Graph from "~/components/OrganizationShow/Pipeline/Graph";
import BranchSettingsDialog from "~/components/OrganizationShow/Pipeline/BranchSettingsDialog";
import {
  BAR_HEIGHT_MINIMUM,
  GRAPH_HEIGHT,
  GRAPH_WIDTH,
} from "~/components/OrganizationShow/Pipeline/constants";

import EmojiAvatar from "~/components/shared/EmojiAvatar";

type Props = {
  pipeline: any;
  includeGraphData: boolean;
  relay: RelayRefetchProp;
  onTagClick: (tag: string) => void;
};

type State = {
  showingMenu: boolean;
  showingBranchSettingsDialog: boolean;
};

const PipelineActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  width: 30px;
  background-color: ${getCssValue("--slate-100")};
  border-radius: 4px;
  cursor: pointer;
`;

const PipelineContainer = styled.div.attrs({
  "data-testid": "pipeline",
})`
  min-height: 82px;
  min-height: 120px;
  border-radius: 5px;

  .pipeline-actions {
    width: 100%;
    opacity: 0;
    transition: opacity 0.2s;
    will-change: opacity;
  }

  .pipeline-status {
    transition: opacity 0.3s;
    will-change: opacity;
  }

  ${PipelineActionButton} {
    transform: scale(0);
    transition: transform 0.3s;
    will-change: transform;
  }

  &:hover,
  &:focus,
  &:active,
  &:focus-visible,
  &:focus-within {
    .pipeline-actions {
      opacity: 1;
    }
    .pipeline-status {
      opacity: 0;
    }
    ${PipelineActionButton} {
      transform: scale(1);
    }
  }
`;

const PipelineActionIcon = styled(Icon)`
  color: ${getCssValue("--charcoal-700")};
`;

class Pipeline extends React.Component<Props, State> {
  state = {
    showingMenu: false,
    showingBranchSettingsDialog: false,
  };

  actionsDropdown: Dropdown | null | undefined;

  subscription: any;

  componentDidMount() {
    this.subscription = cable.subscriptions.create(
      { channel: "Pipelines::PipelineChannel", uuid: this.props.pipeline.uuid },
      {
        component: this,
        received({ event }) {
          if (event === "stats:changed") {
            this.component.reload({ force: true });
          }
        },
      },
    );
  }

  componentWillUnmount() {
    // Jest mocks subscription creation, which means we won't have a
    // subscription here during tests
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  render() {
    return (
      <PipelineContainer
        data-pipeline={this.props.pipeline.uuid}
        className="depth xs-px4 px6 gap3 md-gap4 flex items-center bg-white antialiased"
      >
        <div className="flex flex-none items-center">
          <EmojiAvatar emoji={this.props.pipeline.emoji} color={this.props.pipeline.color} />
        </div>

        <div className="min-w-0 flex-auto">
          <a
            href={this.props.pipeline.url}
            className="text-decoration-none color-inherit flex min-w-0 flex-auto items-center"
          >
            <div className="max-w-full truncate">
              <div className="flex items-center">
                <h2
                  className="black semi-bold m-0 inline max-w-full truncate leading-tight"
                  style={{ fontSize: "16px" }}
                >
                  <Emojify text={this.props.pipeline.name} />
                </h2>
                <PipelineStatus public={this.props.pipeline.public} />
              </div>
              {this.renderDescription()}
            </div>
          </a>

          {this.renderTags()}
        </div>

        {this.renderGraph()}

        <div className="flex flex-none items-center justify-start max-md:sr-only">
          <Metrics pipeline={this.props.pipeline} />
        </div>

        <div className="relative flex-none" style={{ width: "44px", height: "60px" }}>
          <div className="pipeline-status absolute left-0 top-0">
            <Status pipeline={this.props.pipeline} />
          </div>

          <div className="pipeline-actions absolute right-0 top-0 bg-white">
            {this.renderActions()}
          </div>
        </div>
      </PipelineContainer>
    );
  }

  renderDescription() {
    if (this.props.pipeline.description) {
      return (
        <div
          className="dark-gray max-w-full truncate"
          style={{ marginTop: 3, color: getCssValue("--charcoal-500") }}
        >
          <Emojify className="text-sm font-normal" text={this.props.pipeline.description} />
        </div>
      );
    }
  }

  renderTags() {
    if (this.props.pipeline.tags.length) {
      return (
        <div className="gap0.5 mt-1 flex flex-wrap items-center">
          {this.props.pipeline.tags.map((tag) => (
            <Tag key={tag.label} tag={tag} onClick={this.props.onTagClick} />
          ))}
        </div>
      );
    }
  }

  renderGraph() {
    // Toggle between showing the graph, or showing a placeholder until the
    // data is finally loaded in.

    let graph;

    if (this.props.includeGraphData) {
      graph = <Graph pipeline={this.props.pipeline} />;
    } else {
      graph = (
        <div style={{ height: GRAPH_HEIGHT, width: GRAPH_WIDTH }} className="flex">
          <div
            style={{
              height: BAR_HEIGHT_MINIMUM,
              width: "100%",
              marginTop: "auto",
            }}
            className="animation-loading-bar bg-gray"
          />
        </div>
      );
    }

    return (
      <div className="flex-none max-lg:sr-only">
        <div className="dark-gray mb-1 text-xs font-normal leading-none">
          <Icon icon="code" style={{ width: 10, height: 10 }} />
          {this.props.pipeline.defaultBranch || "All Builds"}
        </div>
        {graph}
      </div>
    );
  }

  renderActions() {
    // Make sure we're allowed to favorite this pipeline
    const favoriteButton = permissions(this.props.pipeline.permissions).check({
      allowed: "pipelineFavorite",
      render: () => {
        return window.Features.BuildsUsabilityPipelineRowIconsAndStyles ? (
          <PipelineActionButton
            className="unstyled-button no-title-tooltip"
            aria-label={this.props.pipeline.favorite ? "Remove bookmark" : "Bookmark"}
            title={this.props.pipeline.favorite ? "Remove bookmark" : "Bookmark"}
            onClick={this.handleFavoriteClick}
          >
            <PipelineActionIcon
              icon={this.props.pipeline.favorite ? "bookmark-strike" : "bookmark"}
              style={{ height: 24, width: 24 }}
            />
          </PipelineActionButton>
        ) : (
          <button
            key="favorite"
            className="btn dark-gray p-0 leading-none"
            onClick={this.handleFavoriteClick}
          >
            <Favorite favorite={this.props.pipeline.favorite} />
          </button>
        );
      },
    });

    if (window.Features.BuildsUsabilityPipelineRowIconsAndStyles) {
      //TODO when removing the `BuildsUsabilityPipelineRowIconsAndStyles` feature flag:
      // - remove all code in this function below this return block
      // - clean up the BranchSettingsDialog state and component
      return (
        <div className="gap1 ml-auto flex flex-none flex-col items-center justify-center">
          {favoriteButton}
          <PipelineActionButton
            as="a"
            className="no-title-tooltip"
            title="All builds"
            href={`${this.props.pipeline.url}/builds`}
          >
            <PipelineActionIcon icon="table-3-row" style={{ height: 24, width: 24 }} />
          </PipelineActionButton>
        </div>
      );
    }

    // Make sure we can perform the actions inside the dropdown
    const dropdownActions = permissions(this.props.pipeline.permissions).collect(
      {
        always: true,
        render: () => {
          return (
            <a
              key="builds-view"
              href={`${this.props.pipeline.url}/builds`}
              className="btn hover-lime block"
            >
              View Builds
            </a>
          );
        },
      },
      {
        allowed: "pipelineUpdate",
        render: () => {
          return (
            <button
              key="branch-settings"
              className="btn hover-lime block"
              onClick={this.handleBranchSettingsShow}
            >
              Branch Settings
            </button>
          );
        },
      },
      {
        allowed: "pipelineUpdate",
        render: () => {
          return (
            <a
              key="pipeline-settings"
              className="btn hover-lime block"
              href={`${this.props.pipeline.url}/settings`}
            >
              Pipeline Settings
            </a>
          );
        },
      },
    );

    // Only render the dropdown button if there's something to put inside it
    let dropdownButton;
    if (dropdownActions.length > 0) {
      dropdownButton = (
        <Dropdown
          key="dropdown"
          // @ts-expect-error - TS2769 - No overload matches this call.
          align="center"
          width={150}
          onToggle={this.handleMenuToggle}
          ref={(actionsDropdown) => (this.actionsDropdown = actionsDropdown)}
        >
          <button
            className="btn gray hover-dark-gray no-title-tooltip p-0"
            aria-label="More Actions"
          >
            <Icon icon="menu" className={classNames({ "dark-gray": this.state.showingMenu })} />
          </button>
          {dropdownActions}
        </Dropdown>
      );
    }

    let branchSettingsDialog;
    if (this.props.pipeline.permissions.pipelineUpdate.allowed) {
      branchSettingsDialog = (
        <BranchSettingsDialog
          isOpen={this.state.showingBranchSettingsDialog}
          onRequestClose={this.handleBranchSettingsRequestClose}
          pipeline={this.props.pipeline}
        />
      );
    }

    return (
      <div className="ml-auto flex flex-none flex-col justify-center pr-2">
        {favoriteButton}
        {favoriteButton && dropdownButton && <div className="mb-1" />}
        {dropdownButton}
        {branchSettingsDialog}
      </div>
    );
  }

  reload = (option?: RefetchOptions) => {
    const { pipeline, includeGraphData } = this.props;
    this.props.relay.refetch(
      {
        slug: `${pipeline.organization.slug}/${pipeline.slug}`,
        includeGraphData,
      },
      null,
      null,
      option,
    );
  };

  handleWebsocketEvent = (payload: any) => {
    if (payload.subevent === "project:updated" && payload.graphql.id === this.props.pipeline.id) {
      this.reload({ force: true });
    }
  };

  handleBranchSettingsShow = () => {
    if (this.state.showingMenu && this.actionsDropdown) {
      this.actionsDropdown.setShowing(false);
      this.setState({ showingMenu: false });
    }

    this.setState({ showingBranchSettingsDialog: true });
  };

  handleBranchSettingsRequestClose = () => {
    this.setState({ showingBranchSettingsDialog: false });
  };

  handleMenuToggle = (visible: any) => {
    this.setState({ showingMenu: visible });
  };

  handleFavoriteClick = () => {
    const environment = Environment.get();
    const { pipeline } = this.props;
    const input = { id: pipeline.id, favorite: !pipeline.favorite } as const;

    commitMutation(environment, {
      mutation: graphql`
        mutation PipelineFavoriteMutation($input: PipelineFavoriteInput!) {
          pipelineFavorite(input: $input) {
            pipeline {
              favorite
            }
          }
        }
      `,
      variables: { input },
      optimisticResponse: { pipelineFavorite: { pipeline: input } },
    });
  };
}

/*
Disabling @graphql-eslint/no-deprecated here since public is deprecated
visibility is prefered over public
*/
export default createRefetchContainer(
  Pipeline,
  {
    pipeline: graphql`
      fragment Pipeline_pipeline on Pipeline
      @argumentDefinitions(includeGraphData: { type: "Boolean!" }) {
        ...Status_pipeline
        ...Metrics_pipeline
        ...BranchSettingsDialog_pipeline
        ...Graph_pipeline @arguments(includeGraphData: $includeGraphData)
        id
        uuid
        name
        slug
        #eslint-disable-next-line @graphql-eslint/no-deprecated
        public
        description
        defaultBranch
        url
        favorite
        color
        emoji
        tags {
          ...Tag_tag
          label
        }
        permissions {
          pipelineFavorite {
            allowed
          }
          pipelineUpdate {
            allowed
          }
        }
        organization {
          slug
        }
      }
    `,
  },
  graphql`
    query PipelineRefetchQuery($slug: ID!, $includeGraphData: Boolean!) {
      pipeline(slug: $slug) {
        ...Pipeline_pipeline @arguments(includeGraphData: $includeGraphData)
      }
    }
  `,
);
